import {
  RECHARGE_ADMIN_URL,
  RENEWAL_DISCOUNT_APPLICATION_STATUS,
  RENEWAL_DISCOUNT_SOURCE,
  formatParisDate,
  formatParisDateTime,
  parseParisDayKey,
  renewalDiscountApplicationStatusSchema,
  renewalDiscountHistoryAction,
  statusEntries,
  type RenewalDiscountApplicationListItem,
  type RenewalDiscountApplicationStatus,
  type RenewalDiscountDetail,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { createDataTableColumnHelper, DataTable } from '@/components/data-table/data-table.js';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { PageSection } from '@/components/layout/page.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { Dash, DetailSection, DetailSkeleton, DetailSummary, ExternalRechargeLink } from '../subscriptions/detail-parts.js';
import { DetailSheet, useDetailSheetParam } from '../subscriptions/detail-sheet.js';
import { formatEuros } from '../subscriptions/money.js';
import { fetchRenewalApplication, fetchRenewalApplications } from './api.js';
import { StatusCounts } from './renewal-discount-preview-tab.js';
import { RenewalRunsSection } from './renewal-discount-runs.js';

const PAGE_SIZE = 50;
const ALL_STATUSES = '__all__';
const NUMERIC_COLUMNS = ['amountBefore', 'discountAmount'] as const;

/** Liste des clients du Suivi, portée par l'URL (`routes/operation-detail.tsx`). */
interface TrackingSearch {
  page: number;
  status: RenewalDiscountApplicationStatus | undefined;
  search: string;
}

function readTrackingSearch(rawSearch: unknown): TrackingSearch {
  const record = typeof rawSearch === 'object' && rawSearch !== null ? (rawSearch as Record<string, unknown>) : {};
  const status = renewalDiscountApplicationStatusSchema.safeParse(record.clientStatus);
  const page = Number(record.clientPage);
  return {
    page: Number.isInteger(page) && page > 0 ? page : 1,
    status: status.success ? status.data : undefined,
    search: typeof record.clientSearch === 'string' ? record.clientSearch : '',
  };
}

function day(value: string | null): JSX.Element {
  return value === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{formatParisDate(parseParisDayKey(value))}</span>;
}

function euros(value: string | null): JSX.Element {
  const formatted = formatEuros(value);
  return formatted === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{formatted}</span>;
}

const helper = createDataTableColumnHelper<RenewalDiscountApplicationListItem>();

const columns = helper.columns([
  helper.display({
    id: 'customer',
    header: 'Client',
    cell: ({ row }) => (
      <span className="flex min-w-0 max-w-56 flex-col">
        <span className="truncate" title={row.original.email ?? row.original.customerId} translate="no">
          {row.original.email ?? row.original.customerId}
        </span>
        {row.original.email === null ? null : (
          <span className="text-xs text-muted-foreground" translate="no">
            {row.original.customerId}
          </span>
        )}
      </span>
    ),
  }),
  helper.accessor('chargeId', {
    header: 'Charge',
    cell: (info) => {
      const chargeId = info.getValue();
      return chargeId === null ? <Dash /> : <span translate="no">{chargeId}</span>;
    },
  }),
  helper.accessor('scheduledAt', { header: 'Renouvellement', cell: (info) => day(info.getValue()) }),
  helper.accessor('amountBefore', { header: 'Avant', cell: (info) => euros(info.getValue()) }),
  helper.accessor('discountAmount', { header: 'Remise', cell: (info) => euros(info.getValue()) }),
  helper.accessor('status', {
    header: 'Statut',
    cell: (info) => <StatusBadge registry={RENEWAL_DISCOUNT_APPLICATION_STATUS} code={info.getValue()} />,
  }),
  helper.accessor('source', {
    header: 'Déclencheur',
    cell: (info) => (
      <span className="flex flex-wrap items-center gap-1.5">
        <StatusBadge registry={RENEWAL_DISCOUNT_SOURCE} code={info.getValue()} />
      </span>
    ),
  }),
]);

function ApplicationSheet({ operationId, applicationId, onClose }: { operationId: string; applicationId: string | null; onClose: () => void }): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.operations.renewalApplication(operationId, applicationId ?? ''),
    queryFn: () => fetchRenewalApplication(operationId, applicationId ?? ''),
    enabled: applicationId !== null,
  });
  const detail = query.data;
  return (
    <DetailSheet open={applicationId !== null} onClose={onClose} title={detail?.email ?? detail?.customerId ?? 'Client'}>
      {query.isPending ? (
        <DetailSkeleton label="Chargement du client" />
      ) : query.isError || detail === undefined ? (
        query.error instanceof ApiError && query.error.status === 404 ? (
          <ErrorState message={query.error.message} />
        ) : (
          <ErrorState
            message={query.error instanceof ApiError ? query.error.message : 'Impossible de charger ce client.'}
            detail={query.error instanceof ApiError ? query.error.detail : undefined}
            onRetry={() => {
              void query.refetch();
            }}
          />
        )
      ) : (
        <div className="flex flex-col gap-5">
          <DetailSummary title="Remise">
            <DescriptionList>
              <DescriptionItem label="Statut">
                <StatusBadge registry={RENEWAL_DISCOUNT_APPLICATION_STATUS} code={detail.status} />
              </DescriptionItem>
              <DescriptionItem label="Renouvellement">
                {day(detail.scheduledAt)}
                {detail.chargeId === null ? null : <span className="ml-1 text-xs text-muted-foreground">
                    charge <span translate="no">{detail.chargeId}</span>
                  </span>}
              </DescriptionItem>
              <DescriptionItem label="Produits">
                <span className="tabular-nums">
                  {detail.productCount}
                  {detail.percent === null ? '' : ` · palier ${String(detail.percent).replace('.', ',')} %`}
                </span>
              </DescriptionItem>
              <DescriptionItem label="Prix avant">{euros(detail.amountBefore)}</DescriptionItem>
              {detail.targetAmount === null ? null : <DescriptionItem label="Prix visé">{euros(detail.targetAmount)}</DescriptionItem>}
              <DescriptionItem label="Remise">{euros(detail.discountAmount)}</DescriptionItem>
              <DescriptionItem label="Code posé">
                {detail.discountCode === null ? <Dash /> : <span className="font-mono text-xs" translate="no">{detail.discountCode}</span>}
              </DescriptionItem>
              <DescriptionItem label="Code du client">
                {detail.previousCode === null ? <Dash /> : <span className="font-mono text-xs" translate="no">{detail.previousCode}</span>}
              </DescriptionItem>
              <DescriptionItem label="Déclencheur">
                <StatusBadge registry={RENEWAL_DISCOUNT_SOURCE} code={detail.source} />
              </DescriptionItem>
            </DescriptionList>
            {detail.error === null ? null : <p className="text-sm text-status-red-fg">{detail.error}</p>}
            <ExternalRechargeLink href={`${RECHARGE_ADMIN_URL}/customers/${encodeURIComponent(detail.customerId)}`}>
              Ouvrir le client dans Recharge
            </ExternalRechargeLink>
          </DetailSummary>
          <DetailSection title="Historique">
            {detail.history.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun événement.</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {detail.history.map((entry, index) => (
                  <li key={`${entry.at}-${String(index)}`} className="flex flex-col gap-0.5 text-sm">
                    <span className="flex flex-wrap items-center gap-2">
                      {(() => {
                        const action = renewalDiscountHistoryAction(entry.action);
                        return <Badge tone={action.tone}>{action.label}</Badge>;
                      })()}
                      <span className="text-xs tabular-nums text-muted-foreground">{formatParisDateTime(new Date(entry.at))}</span>
                      {entry.chargeId === null ? null : <span className="text-xs text-muted-foreground">
                          charge <span translate="no">{entry.chargeId}</span>
                        </span>}
                    </span>
                    {entry.detail === null ? null : <span className="text-muted-foreground">{entry.detail}</span>}
                  </li>
                ))}
              </ol>
            )}
          </DetailSection>
        </div>
      )}
    </DetailSheet>
  );
}

export interface RenewalTrackingTabProps {
  operationId: string;
  config: RenewalDiscountDetail;
}

/** Onglet « Suivi » (`07` §6.4) : compteurs, dernières exécutions, clients. */
export function RenewalTrackingTab({ operationId, config }: RenewalTrackingTabProps): JSX.Element {
  const navigate = useNavigate();
  const { page, status, search } = readTrackingSearch(useSearch({ strict: false }));
  const sheet = useDetailSheetParam('client');
  const running = config.lastRuns.some((run) => run.status === 'running');
  const params = { page, pageSize: PAGE_SIZE, ...(status === undefined ? {} : { status }), ...(search === '' ? {} : { search }) };
  const isFiltered = status !== undefined || search !== '';

  const query = useQuery({
    queryKey: queryKeys.operations.renewalApplications(operationId, params),
    queryFn: () => fetchRenewalApplications(operationId, params),
    refetchInterval: running ? 2_000 : false,
  });

  /** Filtres et page dans l'URL, en `replace` : seul le panneau d'un client pousse une entrée. */
  function updateSearch(patch: { clientStatus?: string | undefined; clientSearch?: string | undefined; clientPage?: number | undefined }): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
      replace: true,
    });
  }

  function resetFilters(): void {
    updateSearch({ clientStatus: undefined, clientSearch: undefined, clientPage: undefined });
  }

  const emptyState =
    config.state === 'draft'
      ? { title: 'Aucun client', description: 'Les clients apparaîtront après l’activation de l’opération.' }
      : isFiltered
        ? {
            title: 'Aucun client ne correspond à ces filtres',
            description: 'Modifiez la recherche ou le statut, ou réinitialisez les filtres.',
            action: (
              <Button type="button" variant="outline" onClick={resetFilters}>
                Réinitialiser
              </Button>
            ),
          }
        : {
            title: 'Aucun client pour l’instant',
            description:
              'La passe initiale ajoute ici les clients dont le renouvellement tombe dans la période ; les webhooks et la réconciliation quotidienne complètent la liste au fil de l’eau.',
          };

  return (
    <div className="flex flex-col gap-6">
      <PageSection title="Clients">
        <div className="flex flex-col gap-3">
          <StatusCounts counts={config.counts} />
          <p className="text-sm tabular-nums text-muted-foreground">
            {`Remises posées ou consommées : ${formatEuros(config.totalDiscount) ?? '—'}`}
          </p>
        </div>
      </PageSection>

      <RenewalRunsSection runs={config.lastRuns} />

      <DataTable
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={(next) => {
          updateSearch({ clientPage: next === 1 ? undefined : next });
        }}
        isLoading={query.isPending}
        isError={query.isError}
        {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        onRowClick={(row) => {
          sheet.openDetail(row.id);
        }}
        toolbar={
          <ListToolbar
            search={{
              value: search,
              onChange: (next) => {
                updateSearch({ clientSearch: next === '' ? undefined : next, clientPage: undefined });
              },
              placeholder: 'Email, client ou charge…',
            }}
            isFiltered={isFiltered}
            onReset={resetFilters}
          >
            <Select
              value={status ?? ALL_STATUSES}
              onValueChange={(next) => {
                const parsed = renewalDiscountApplicationStatusSchema.safeParse(next);
                updateSearch({ clientStatus: parsed.success ? parsed.data : undefined, clientPage: undefined });
              }}
            >
              <SelectTrigger className="w-48" aria-label="Statut">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES}>Tous les statuts</SelectItem>
                {statusEntries(RENEWAL_DISCOUNT_APPLICATION_STATUS).map((entry) => (
                  <SelectItem key={entry.code} value={entry.code}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ListToolbar>
        }
        emptyState={emptyState}
      />

      <ApplicationSheet operationId={operationId} applicationId={sheet.id} onClose={sheet.closeDetail} />
    </div>
  );
}
